import { COMPONENT_COLOR_THEMES, COMPONENT_VALUE_TYPES as types, HIGHLIGHTS_DETAIL_TEXT_TYPE, HIGHLIGHTS_DETAIL_LAYOUT, APP_HANDLERS, FONT_SIZES, TEXT_PROPS_VALIDATION } from 'shared/constants';
import type { PropsSchemaList } from 'shared/types/PropsSchema';
import type { Props as HighlightsProps } from './HighlightsDetail/highlightsDetailProps';
import {
  copySocialMediaMessageLayoutPropsSchema,
  highlightsDefaultProps2,
  highlightsDefaultProps3,
  highlightsDefaultProps4,
  highlightsDefaultProps5
} from './HighlightsDetail/highlightsDetailProps';

export type ColorTheme = 'LIGHT' | 'DARK';

export type Position = 'TOP' | 'BOTTOM' | 'LEFT' | 'RIGHT' | 'CENTER';

export interface Props {
  slides: {
    id: string;
    navigatorLabel: string;
    navigatorTheme: string;
    topSubtitle?: string;
    title: string;
    subtitle?: string;
    subtitleCarousel?: {
      text?: string;
    }[];
    reverseSubtitleCarouselAnimationDirection?: boolean;
    imageUrl: {
      _: string;
      md?: string;
      imageAlt?: string;
    };
    infoBlock?: {
      colorTheme: ColorTheme;
      position: {
        _: Position,
        md: Position
      };
      topSubtitle: string;
      title: string;
      bottomSubtitle: string;
      infoList: {
        title: string;
        subtitle: string;
      }[];
      cta: {
        label: string;
        url: string;
        appHandlerKey?: string;
        hideOnMobile?: boolean;
      }
    },
    specs: {
      title: string;
      subtitle: string;
    }[];
    cta: {
      text: string;
      url: string;
      appHandlerKey?: string;
      disabled?: boolean;
    }[];
    detail: {
      title: string;
      subtitle: string;
      description: string;
    };
    highlights?: HighlightsProps;
  }[],
  navigatorEnabled?: boolean;
  backNavigation?: boolean;
  alwaysShowDownArrow?: boolean;
  showDownArrowLastSlide?: boolean;
  slideScrollSnapAnimation?: boolean;
  floatingCta?: {
    label?: string;
    url?: string;
    appHandlerKey?: string;
  }
}

const propsSchema: PropsSchemaList = [
  {
    key: 'slides',
    label: 'Slides',
    type: types.ARRAY,
    children: [
      {
        key: 'id',
        label: 'Slide Id',
        type: types.TEXT,
      },
      {
        key: 'navigatorLabel',
        label: 'Navigator Label',
        type: types.TEXT,
      },
      {
        key: 'navigatorTheme',
        label: 'Navigator Theme',
        type: types.ENUM,
        options: COMPONENT_COLOR_THEMES,
      },
      {
        key: 'topSubtitle',
        label: 'Top Subtitle',
        type: types.TEXT,
      },
      {
        key: 'title',
        label: 'Title',
        type: types.TEXT,
      },
      {
        key: 'subtitle',
        label: 'Subtitle',
        type: types.TEXT,
      },
      {
        key: 'subtitleCarousel',
        label: 'Subtitle Carousel',
        type: types.ARRAY,
        children: [
          {
            key: 'text',
            label: 'Text',
            type: types.TEXT,
          },
        ]
      },
      {
        key: 'reverseSubtitleCarouselAnimationDirection',
        label: 'Reverse Subtitle Carousel Animation Direction',
        type: types.BOOLEAN,
      },
      {
        key: 'imageUrl',
        label: 'Slide Background Image Url',
        type: types.OBJECT,
        children: [
          {
            key: '_',
            label: 'Mobile (or Base)',
            type: types.S3_ASSET_URL,
            options: {
              validation: TEXT_PROPS_VALIDATION.PRODUCT_SLIDE_SHOW_S3_ASSET_URL_MOBILE,
            },
          },
          {
            key: 'md',
            label: 'Desktop',
            type: types.S3_ASSET_URL,
            options: {
              validation: TEXT_PROPS_VALIDATION.PRODUCT_SLIDE_SHOW_S3_ASSET_URL_DESKTOP,
            },
          },
          {
            key: 'imageAlt',
            label: 'Image Alternative Text (A brief description of the image to optimize search engine visibility)',
            type: types.TEXT,
          },
        ],
      },
      {
        key: 'infoBlock',
        label: 'Info Block',
        type: types.OBJECT,
        children: [
          {
            key: 'colorTheme',
            label: 'Text Color Theme',
            type: types.ENUM,
            options: COMPONENT_COLOR_THEMES,
          },
          {
            key: 'position',
            label: 'Position',
            type: types.OBJECT,
            children: [
              {
                key: '_',
                label: 'Mobile',
                type: types.ENUM,
                // center is default
                options: ['CENTER', 'TOP', 'BOTTOM', 'LEFT', 'RIGHT'],
              },
              {
                key: 'md',
                label: 'Desktop',
                type: types.ENUM,
                // center is default
                options: ['CENTER', 'TOP', 'BOTTOM', 'LEFT', 'RIGHT'],
              },
            ],
          },
          {
            key: 'topSubtitle',
            label: 'Top Subtitle',
            type: types.TEXT,
          },
          {
            key: 'title',
            label: 'Title',
            type: types.TEXT,
          },
          {
            key: 'bottomSubtitle',
            label: 'Bottom Subtitle',
            type: types.TEXT,
          },
          {
            key: 'infoList',
            label: 'Info List',
            type: types.ARRAY,
            children: [
              {
                key: 'title',
                label: 'Title',
                type: types.TEXT,
              },
              {
                key: 'subtitle',
                label: 'Subtitle',
                type: types.TEXT,
              },
            ]
          },
          {
            key: 'cta',
            label: 'CTA',
            type: types.OBJECT,
            children: [
              {
                key: 'label',
                label: 'Label',
                type: types.TEXT,
              },
              {
                key: 'url',
                label: 'URL',
                type: types.TEXT,
              },
              {
                key: 'appHandlerKey',
                label: 'Use App Handler',
                type: types.ENUM,
                options: APP_HANDLERS,
              },
              {
                key: 'hideOnMobile',
                label: 'Hide On Mobile',
                type: types.BOOLEAN,
              },
            ]
          },
        ],
      },
      {
        key: 'specs',
        label: 'Specs',
        type: types.ARRAY,
        children: [
          {
            key: 'title',
            label: 'Title',
            type: types.TEXT,
          },
          {
            key: 'subtitle',
            label: 'Subtitle',
            type: types.TEXT,
          },
        ],
      },
      {
        key: 'cta',
        label: 'CTA Buttons',
        type: types.ARRAY,
        children: [
          {
            key: 'text',
            label: 'Text',
            type: types.TEXT,
          },
          {
            key: 'appHandlerKey',
            label: 'Use App Handler',
            type: types.ENUM,
            options: APP_HANDLERS,
          },
          {
            key: 'url',
            label: 'URL',
            type: types.TEXT,
          },
          {
            key: 'disabled',
            label: 'Disabled',
            type: types.BOOLEAN,
          },
        ],
      },
      {
        key: 'detail',
        label: 'Detail',
        type: types.OBJECT,
        children: [
          {
            key: 'title',
            label: 'Title',
            type: types.TEXT,
          },
          {
            key: 'subtitle',
            label: 'Subtitle',
            type: types.TEXT,
          },
          {
            key: 'description',
            label: 'Description',
            type: types.TEXT,
          },
        ],
      },
      {
        key: 'highlights',
        label: 'Learn More Modal',
        type: types.OBJECT,
        children: [
          {
            key: 'learnMore',
            label: 'Learn More Text',
            type: types.OBJECT,
            children: [
              {
                key: 'learnMoreText',
                label: 'Text',
                type: types.TEXT
              },
              {
                key: 'learnMoreFontSizeDesktop',
                label: 'Desktop font size',
                type: types.ENUM,
                options: FONT_SIZES,
              },
              {
                key: 'learnMoreFontSizeMobile',
                label: 'Mobile font size',
                type: types.ENUM,
                options: FONT_SIZES,
              },
            ]
          },
          {
            key: 'slides',
            label: 'Slides',
            type: types.ARRAY,
            children: [
              {
                key: 'layout',
                label: 'Layout',
                type: types.ENUM,
                options: HIGHLIGHTS_DETAIL_LAYOUT,
              },
              {
                key: 'slideNumber',
                label: 'Slide Number',
                type: types.OBJECT,
                tooltip: 'Optional',
                children: [
                  {
                    key: 'number',
                    label: 'Slide Number',
                    type: types.TEXT,
                  },
                  {
                    key: 'content',
                    label: 'Content',
                    type: types.TEXT,
                  },
                ],
              },
              {
                key: 'text',
                label: 'Text',
                type: types.ARRAY,
                children: [
                  {
                    key: 'type',
                    label: 'Type',
                    type: types.ENUM,
                    options: HIGHLIGHTS_DETAIL_TEXT_TYPE,
                  },
                  {
                    key: 'content',
                    label: 'Content',
                    type: types.TEXTAREA,
                  },
                ],
              },
              {
                key: 'asset',
                label: 'Asset',
                type: types.ARRAY,
                children: [
                  {
                    key: 'src',
                    label: 'Image',
                    type: types.S3_ASSET_URL,
                  },
                  {
                    key: 'imageAlt',
                    label: 'Image Alternative Text (A brief description of the image to optimize search engine visibility)',
                    type: types.TEXT,
                  },
                  {
                    key: 'extendWidth',
                    label: 'Extend Image Width',
                    type: types.BOOLEAN,
                  },
                ],
              },
              {
                key: 'link',
                label: 'Link',
                type: types.ARRAY,
                children: [
                  {
                    key: 'src',
                    label: 'Link',
                    type: types.TEXT,
                  },
                  {
                    key: 'appHandlerKey',
                    label: 'Use App Handler',
                    type: types.ENUM,
                    options: APP_HANDLERS,
                  },
                  {
                    key: 'content',
                    label: 'Content',
                    type: types.TEXT,
                  },
                ],
              },
              {
                key: 'button',
                label: 'Button',
                type: types.ARRAY,
                children: [
                  {
                    key: 'src',
                    label: 'Link',
                    type: types.TEXT,
                  },
                  {
                    key: 'appHandlerKey',
                    label: 'Use App Handler',
                    type: types.ENUM,
                    options: APP_HANDLERS,
                  },
                  {
                    key: 'content',
                    label: 'Content',
                    type: types.TEXT,
                  },
                ],
              },
              {
                key: 'socialFeed',
                label: 'Social Feed',
                type: types.OBJECT,
                children: copySocialMediaMessageLayoutPropsSchema,
              },
            ],
          },
          {
            key: 'socialLinks',
            label: 'Share Drawer: Social Media Links',
            type: types.OBJECT,
            children: [
              {
                key: 'mobileDeviceShareLink',
                label: 'Mobile Device Share Link',
                type: types.OBJECT,
                children: [
                  {
                    key: 'url',
                    label: 'Share Link',
                    type: types.TEXT,
                  },
                  {
                    key: 'title',
                    label: 'Title',
                    type: types.TEXT,
                  },
                  {
                    key: 'text',
                    label: 'Text',
                    type: types.TEXT,
                  },
                  {
                    key: 'imageUrl',
                    label: 'Thumbnail Image',
                    type: types.S3_ASSET_URL,
                  },
                ],
              },
              {
                key: 'desktopDeviceShareLink',
                label: 'Desktop Device Share Link to Social Media',
                type: types.ARRAY,
                children: [
                  {
                    key: 'icon',
                    label: 'Icon',
                    tooltip: 'WeChat icon displays a QR Code of the Share Link',
                    type: types.ICON,
                  },
                  {
                    key: 'text',
                    label: 'Icon Text',
                    type: types.TEXT,
                  },
                  {
                    key: 'url',
                    label: 'Share Link',
                    type: types.TEXT,
                  },
                ],
              },
              {
                key: 'desktopDeviceShareEmailLink',
                label: 'Share Link via Email',
                type: types.ARRAY,
                children: [
                  {
                    key: 'icon',
                    label: 'icon',
                    type: types.ICON,
                  },
                  {
                    key: 'text',
                    label: 'Icon Text',
                    type: types.TEXT,
                  },
                  {
                    key: 'emailSubject',
                    label: 'Email Subject',
                    type: types.TEXT,
                  },
                  {
                    key: 'emailBody',
                    label: 'Email Body',
                    type: types.TEXT,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    key: 'navigatorEnabled',
    label: 'Enabled navigator',
    type: types.BOOLEAN,
  },
  {
    key: 'backNavigation',
    label: 'Enabled back navigation for App',
    type: types.BOOLEAN,
  },
  {
    key: 'shareNavigation',
    label: 'Enabled share button for App',
    type: types.BOOLEAN,
  },
  {
    key: 'alwaysShowDownArrow',
    label: 'Always show down arrow on both desktop and mobile, otherwise, show on desktop only',
    type: types.BOOLEAN,
  },
  {
    key: 'showDownArrowLastSlide',
    label: 'Should down arrow show on last slide',
    type: types.BOOLEAN,
  },
  {
    key: 'slideScrollSnapAnimation',
    label: 'Slide Scroll Snap Animation',
    type: types.BOOLEAN,
  },
  {
    key: 'floatingCta',
    label: 'Floating CTA only for h5 APP',
    type: types.OBJECT,
    children: [
      {
        key: 'label',
        label: 'Label',
        type: types.TEXT,
      },
      {
        key: 'url',
        label: 'URL',
        type: types.TEXT,
      },
      {
        key: 'appHandlerKey',
        label: 'Use App Handler',
        type: types.ENUM,
        options: APP_HANDLERS,
      },
    ]
  },
];

const defaultProps: Props = {
  slides: [
    {
      id: '1',
      navigatorLabel: 'FF 91 Futurist Alliance',
      navigatorTheme: 'LIGHT',
      title: 'FF 91 Futurist Alliance',
      subtitle: 'Preorder Online for Touchless Delivery',
      reverseSubtitleCarouselAnimationDirection: false,
      imageUrl: {
        _: 'https://genesis-cdn.ff.com/faraday-future-gallery/ff-91/ff-91-gallery6.jpg',
        md: 'https://ff-genesis-cdn.s3.us-west-2.amazonaws.com/faraday-future-gallery/ff-91/accd-8701%20ADJUSTED.jpg',
        imageAlt: 'FF 91',
      },
      specs: [
        {
          title: '381mi',
          subtitle: 'Range (est.)'
        },
        {
          title: '155mph',
          subtitle: 'EPA Range'
        },
        {
          title: '2.37s',
          subtitle: '0-60 mph'
        },
        {
          title: 'AWD',
          subtitle: 'Three Motors'
        },
      ],
      cta: [
        {
          text: 'Test Drive',
          url: ''
        },
        {
          text: 'Order Now',
          url: 'https://www.ff.com/us/preorder'
        }
      ],
      detail: {
        title: '',
        subtitle: '',
        description: ''
      }
    },
    {
      id: '2',
      navigatorLabel: 'Performance',
      navigatorTheme: 'DARK',
      title: '',
      subtitle: '',
      reverseSubtitleCarouselAnimationDirection: false,
      imageUrl: {
        _: 'https://ff-genesis-cdn-dev.s3.amazonaws.com/WX20221206-162138-1-default.jpg',
        md: 'https://genesis-cdn.ff.com/faraday-future-gallery/ff-91-testing/ff-viceland-tylerthecreator-fb1.jpg',
      },
      specs: [
        {
          title: '381mi',
          subtitle: 'EPA Range'
        },
        {
          title: '2.27s',
          subtitle: '0-60 mph'
        },
        {
          title: '0',
          subtitle: 'Gravity Rear Seats'
        },
      ],
      cta: [
        {
          text: 'Order Now',
          url: 'https://www.ff.com/us/preorder'
        }
      ],
      detail: {
        title: 'Universal Vehicle Abilities',
        subtitle: 'Performance',
        description: `
        The endless capabilities of FF 91 come from reimaging what a vehicle can do. 
        By removing standard limits and expectations of mobility, 
        FF has merged the vehicle genres of sport, 
        SUV and sedan into something entirely new. 
        As a result, unique User need are precisely addressedv - 
        in every seats - with innovative Vehicle abilities.
        `,
      },
      highlights: highlightsDefaultProps2,
    },
    {
      id: '3',
      navigatorLabel: 'Exterior',
      navigatorTheme: 'LIGHT',
      title: 'FF 91 Futurist Alliance',
      subtitle: 'Preorder Online for Touchless Delivery',
      reverseSubtitleCarouselAnimationDirection: false,
      imageUrl: {
        _: 'https://ff-genesis-cdn-dev.s3.amazonaws.com/WX20221206-162138-1-default.jpg'
      },
      specs: [
        {
          title: '1050hp',
          subtitle: 'Tri motor'
        },
        {
          title: '142kWh',
          subtitle: 'Battery'
        },
        {
          title: '4',
          subtitle: 'Drive Modes'
        },
      ],
      cta: [
        {
          text: 'Order Now',
          url: 'https://www.ff.com/us/preorder'
        }
      ],
      detail: {
        title: 'Hyper-Dynamic Driving Platform',
        subtitle: 'Exterior',
        description: `FF 91 Futurist’s instantaneous power is class-leading and applied with hyper-precision. 
        Through a combination of computing power and mechanical ingenuity, 
        its extreme agility sets a new standard for the electrified driving experience.
        `,
      },
      highlights: highlightsDefaultProps3,
    },
    {
      id: '4',
      navigatorLabel: 'Interior',
      navigatorTheme: 'LIGHT',
      title: '',
      subtitle: '',
      reverseSubtitleCarouselAnimationDirection: false,
      imageUrl: {
        _: 'https://ff-genesis-cdn-qa.s3.amazonaws.com/configurator/design/images/interior/Interior_Porcelaine-1800w.jpg'
      },
      specs: [
        {
          title: '110+ Inch',
          subtitle: '11 displays'
        },
        {
          title: 'Live TV and Video',
          subtitle: 'Conferencing'
        },
        {
          title: '5G Mobile',
          subtitle: 'Connectivity'
        },
      ],
      cta: [
        {
          text: 'Order Now',
          url: 'https://www.ff.com/us/preorder'
        }
      ],
      detail: {
        title: 'TechLuxury Living Space',
        subtitle: 'Interior',
        description: `
        The endless capabilities of FF 91 come from reimaging what a vehicle can do. 
        By removing standard limits and expectations of mobility, 
        FF has merged the vehicle genres of sport, 
        SUV and sedan into something entirely new. 
        As a result, unique User need are precisely addressedv - 
        in every seats - with innovative Vehicle abilities.
        `,
      },
      highlights: highlightsDefaultProps4,
    },
    {
      id: '5',
      navigatorLabel: 'Intelligent Autono AI',
      navigatorTheme: 'LIGHT',
      title: '',
      subtitle: '',
      reverseSubtitleCarouselAnimationDirection: false,
      imageUrl: {
        _: 'https://ff-genesis-cdn-dev.s3.amazonaws.com/Rectangle-1860-default.jpg'
      },
      specs: [
        {
          title: '29 Advanced',
          subtitle: 'Sensors'
        },
        {
          title: 'Orin Autonomous',
          subtitle: 'Driving Computer'
        },
        {
          title: 'FF AI',
          subtitle: '-'
        },
      ],
      cta: [
        {
          text: 'Order Now',
          url: 'https://www.ff.com/us/preorder'
        }
      ],
      detail: {
        title: 'Intelligent Autono AI',
        subtitle: 'Performance',
        description: `
        The endless capabilities of FF 91 come from reimaging what a vehicle can do. 
        By removing standard limits and expectations of mobility, 
        FF has merged the vehicle genres of sport, 
        SUV and sedan into something entirely new. 
        As a result, unique User need are precisely addressedv - 
        in every seats - with innovative Vehicle abilities.
        `,
      },
      highlights: highlightsDefaultProps5,
    },
  ],
  navigatorEnabled: true,
  alwaysShowDownArrow: true,
  slideScrollSnapAnimation: true,
  floatingCta: {
    label: 'Preorder Now',
    url: '/us/preorder',
    appHandlerKey: undefined
  },
};

export default {
  title: 'Product Slide Show',
  id: 'ProductSlideShow',
  propsSchema,
  defaultProps,
}
